// as-cetra-ui/src/components/authenticated/handout/customise-mode/CustomisedHandoutCard.js

import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    IconButton,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import WhiteboardNote from '../whiteboard/WhiteboardNote';
import FavouriteButtonActive from '../../../../assets/icons/FavouriteButtonActive.svg';
import FavouriteButtonDisabled from '../../../../assets/icons/FavouriteButtonDisabled.svg';
import PinkGradientButton from '../../../UIControls/PinkGradientButton';
import { mergeProtectedImageStyle, protectedImageProps } from '../../../../utils/protected-image-props';

const CARD_WIDTH = 369;
const CARD_HEIGHT = 600;
const location = 'en-GB';

const CustomisedHandoutCard = ({
    customisedHandout,
    isMobile,
    userDataStore,
    showSnackbar,
    handleToggleFavourite,
    handleEditCustomisedHandout,
    handleArchiveCustomisedHandout,
    imageMap,
    index,
    actionLabel = 'Archive',
    confirmTitle = 'Are you sure you want to archive this customised handout?',
    confirmBody = 'This action will archive the customised handout and remove it from your list.',
    hideFavourite = false,
    hideEdit = false,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [notesValue, setNotesValue] = useState(customisedHandout.notes || '');

    // keep local notes in sync if parent/store updates
    useEffect(() => {
        setNotesValue(customisedHandout.notes || '');
    }, [customisedHandout.notes]);

    const handleConfirmArchive = () => {
        handleArchiveCustomisedHandout(customisedHandout);
        setConfirmOpen(false);
    };

    // Extract documentTitle from effectiveContentData
    const getDocumentTitle = () => {
        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];
        if (!contentData || !Array.isArray(contentData) || contentData.length === 0) {
            return 'Untitled Handout';
        }

        // Find documentTitle in first section's blocks
        const firstSection = contentData[0];
        if (!firstSection || !firstSection.blocks) {
            return 'Untitled Handout';
        }

        const titleBlock = firstSection.blocks.find((block) => block.type === 'documentTitle');
        return titleBlock?.value || 'Untitled Handout';
    };

    // Extract first paragraph text from effectiveContentData
    const getFirstParagraph = () => {
        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];

        if (!contentData || !Array.isArray(contentData)) {
            return '';
        }

        // Look through sections to find first paragraph or text block
        for (const section of contentData) {
            if (section.blocks) {
                for (const block of section.blocks) {
                    // Check for paragraph, text, or textBullet blocks
                    if (
                        (block.type === 'paragraph' || block.type === 'text' || block.type === 'textBullet') &&
                        block.value
                    ) {
                        // Return the text value, trimmed
                        return block.value.trim();
                    }
                }
            }
        }

        return '';
    };

    // Get image source - either whiteboard image or first illustration
    const getImageSource = () => {
        // First check if attachedWhiteboardCanvasImageLink is in imageMap
        if (
            customisedHandout.attachedWhiteboardCanvasImageLink &&
            imageMap[customisedHandout.attachedWhiteboardCanvasImageLink]
        ) {
            return imageMap[customisedHandout.attachedWhiteboardCanvasImageLink];
        }

        // Otherwise, extract first illustration from effectiveContentData
        const locale = 'en-UK';
        const contentData = customisedHandout.effectiveContentData?.[locale];
        if (!contentData || !Array.isArray(contentData)) {
            return null;
        }

        // Find first illustration block
        for (const section of contentData) {
            if (section.blocks) {
                const illustrationBlock = section.blocks.find((block) => block.type === 'illustration');
                if (illustrationBlock?.src) {
                    // Check if the illustration has been fetched and is in imageMap
                    const illustrationKey = illustrationBlock.src;
                    return imageMap[illustrationKey] || null;
                }
            }
        }

        return null;
    };

    const documentTitle = getDocumentTitle();
    const firstParagraph = getFirstParagraph();
    const imageSource = getImageSource();

    // Format updatedBy name
    const getUpdatedByName = () => {
        const firstName = customisedHandout.updatedByFirstName;
        const lastName = customisedHandout.updatedByLastName;
        return [firstName, lastName].filter(Boolean).join(' ');
    };

    return (
        <Grid
            item
            xs={12}
            md={4}
            key={customisedHandout.customisedId || index}
            pl={index % 3 === 0 ? '0!important' : isMobile ? '0!important' : 2}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {/* Document Title */}
                <Box sx={{ width: isMobile ? '100%' : `${CARD_WIDTH}px` }}>
                    <Tooltip title={documentTitle} arrow>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontSize: '22px',
                                fontWeight: '600',
                                color: '#000453',
                                textAlign: 'left',
                                mt: '10px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {documentTitle}
                        </Typography>
                    </Tooltip>
                </Box>

                {/* Card Panel */}
                <Box
                    sx={{
                        width: '100%',
                        height: `${CARD_HEIGHT}px`,
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        display: 'flex',
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                        mt: '20px',
                        position: 'relative',
                        flexDirection: 'column',
                    }}
                >
                    {/* Favourite Icon */}
                    {!hideFavourite && (
                        <IconButton
                            onClick={() => handleToggleFavourite(customisedHandout.customisedId)}
                            sx={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                width: '32px',
                                height: '32px',
                                padding: 0,
                            }}
                        >
                            <img
                                src={
                                    userDataStore.userData.customisedHandoutsFavourites?.includes(
                                        customisedHandout.customisedId,
                                    )
                                        ? FavouriteButtonActive
                                        : FavouriteButtonDisabled
                                }
                                alt="Favourite"
                                style={{ width: '32px', height: '32px' }}
                            />
                        </IconButton>
                    )}

                    {/* Updated on / By */}
                    <Box sx={{ px: 2, pt: 2, width: '90%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontSize: '22px',
                                color: '#10141F66',
                                minHeight: '26px',
                                lineHeight: '26px',
                                whiteSpace: 'normal',
                                overflowWrap: 'break-word',
                            }}
                        >
                            Updated on: {new Date(customisedHandout.lastUpdated).toLocaleString(location)}
                        </Typography>

                        {userDataStore?.subscriptionData?.accountType?.includes('Clinic') && getUpdatedByName() && (
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontWeight: 500,
                                    fontSize: '22px',
                                    color: '#10141F66',
                                    mt: 0,
                                    minHeight: '26px',
                                    lineHeight: '26px',
                                    whiteSpace: 'normal',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                By: {getUpdatedByName()}
                            </Typography>
                        )}

                        {/* Handout Title */}
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '18px',
                                color: '#000453',
                                mt: 2,
                                lineHeight: '22px',
                            }}
                        >
                            {documentTitle}
                        </Typography>

                        {/* First Paragraph Preview */}
                        {firstParagraph && (
                            <Typography
                                sx={{
                                    fontFamily: 'Inter',
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    color: '#10141F99',
                                    mt: 1,
                                    lineHeight: '18px',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {firstParagraph}
                            </Typography>
                        )}
                    </Box>

                    {/* Image Preview */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            px: 2,
                            py: 1,
                        }}
                    >
                        {imageSource ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    maxHeight: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <img
                                    src={imageSource}
                                    alt={documentTitle}
                                    {...protectedImageProps}
                                    style={mergeProtectedImageStyle({
                                        width: '100%',
                                        height: '800%',
                                        objectFit: 'contain',
                                        maxWidth: '420px',
                                    })}
                                />
                            </Box>
                        ) : (
                            <Typography color="textSecondary">No preview available</Typography>
                        )}
                    </Box>

                    {/* Notes */}
                    <Box sx={{ px: 2, pb: 2 }}>
                        <WhiteboardNote notes={notesValue} />
                    </Box>
                </Box>

                {/* Buttons */}
                <Box
                    component={'section'}
                    sx={{
                        display: 'flex',
                        gap: '12px',
                        width: '100%',
                        mt: '16px',
                        mb: '48px',
                    }}
                >
                    {!hideEdit && (
                        <PinkGradientButton
                            variant="outlined"
                            onClick={() => handleEditCustomisedHandout(customisedHandout)}
                            sx={{
                                height: '40px',
                                borderRadius: '12px',
                                color: 'white',
                                boxShadow: 'none',
                                fontFamily: 'Inter',
                                width: '50%',
                                m: 0,
                            }}
                        >
                            Edit
                        </PinkGradientButton>
                    )}

                    <Button
                        variant="contained"
                        fullWidth={hideEdit}
                        sx={{
                            flex: 1,
                            height: '40px',
                            borderRadius: '12px',
                            backgroundColor: '#000453',
                            color: 'white',
                            boxShadow: 'none',
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            width: '50%',
                            '&:hover': {
                                backgroundColor: '#000331',
                            },
                        }}
                        onClick={() => setConfirmOpen(true)}
                    >
                        {actionLabel}
                    </Button>
                </Box>

                {/* Confirmation Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>{confirmTitle}</DialogTitle>
                    <DialogContent>
                        <Typography>{confirmBody}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmArchive} color="primary" autoFocus>
                            Yes
                        </Button>
                        <Button onClick={() => setConfirmOpen(false)}>No</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Grid>
    );
};

export default observer(CustomisedHandoutCard);
